import type { FC } from 'react';
import React from 'react';

import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import {
	VehicleListEmptyState,
	VehicleListEmptyStateVariant,
} from 'Components/Account/utilities/VehicleListEmptyState/VehicleListEmptyState';
import cypressIds from 'Components/cypress_ids.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { applyCypressData } from 'Utilities/helpers';
import { isQueryLoading } from 'Utilities/helpers/reactQuery.helpers';
import type { UseAccountVehicleListApi } from 'Utilities/hooks/account/useAccountVehicleList';
import { useSuccessToast } from 'Utilities/hooks/account/useUpdateDeliveryStatus/useUpdateDeliveryStatus.helpers';
import { useHandleKycStatus } from 'Utilities/hooks/useHandleKycStatus';

import AccountLoading from '../AccountLoading/AccountLoading';
import AccountNoResults from '../AccountNoResults/AccountNoResults';
import AccountPagination from '../AccountPagination/AccountPagination';

import AccountVehicleCard from './AccountVehicleCard/AccountVehicleCard';

import styles from './AccountVehiclesList.module.scss';

type AccountVehiclesListProps = Pick<VehicleListQuery, 'queryStatus'> & {
	count?: number;
	isPreviousData?: boolean;
	itemsOnPage?: number;
	vehicles?: UseAccountVehicleListApi['results'];
};

const AccountVehiclesList: FC<AccountVehiclesListProps> = ({
	count = 0,
	isPreviousData,
	itemsOnPage,
	queryStatus,
	vehicles = [],
}) => {
	const {
		maxWidth: { breakpointTablet },
	} = useBreakpoint();

	const { isKycCompleted, isKycInProgress, isKycStatusLoading } = useHandleKycStatus({ source: 'AccountVehiclesList' });

	const renderPagination = breakpointTablet;

	const { triggerSuccessToast } = useSuccessToast();

	if (isQueryLoading(queryStatus) || isPreviousData || isKycStatusLoading) {
		return <AccountLoading itemsOnPage={itemsOnPage} />;
	}

	// There is a possibility that the page the user requested is no longer available.
	// For example, the user has 4 pages on the Purchases Completed section, and he clicks on page 4,
	// but just before the click, agent moves the only vehicle on page 4 to `canceled` state.
	// This will end up with empty results page, so it's the reason why we check for vehicles,
	// rather than for the count value.

	if (!count) {
		if (isKycInProgress()) {
			return (
				<div className={styles.kycEmpty}>
					<VehicleListEmptyState variant={VehicleListEmptyStateVariant.EmptyKyc} />
				</div>
			);
		}
		if (isKycCompleted()) {
			return (
				<div className={styles.kycEmptySubmitted}>
					<VehicleListEmptyState variant={VehicleListEmptyStateVariant.EmptyKycSubmitted} />
				</div>
			);
		}
		return <AccountNoResults count={count} />;
	}

	return (
		<div className={styles.accountVehiclesList} {...applyCypressData(cypressIds.lists.accountVehicle)}>
			{vehicles.map((vehicle) => (
				<AccountVehicleCard
					key={vehicle.id}
					className={styles.accountVehicleCard}
					triggerSuccessToast={triggerSuccessToast}
					vehicle={vehicle}
				/>
			))}
			{renderPagination ? <AccountPagination count={count} /> : null}
		</div>
	);
};

export default AccountVehiclesList;
